import { CellHookData } from "jspdf-autotable";
import { Entidad } from "../../../../data/Entidad";
import { PDFThings } from "../../../controlD3/PDFBuilder";
import { UIUtilViewCalificacion } from "../../Calificacion";
import jsPDF from "jspdf";
import LatoBold from '/fonts/Lato/Lato-Bold.ttf?url';
import LatoRegular from '/fonts/Lato/Lato-Regular.ttf?url';

import IBoletaConfig = UIUtilViewCalificacion.IBoletaConfigV2;
import CTipoEvaluacion = Entidad.CTipoEvaluacion;
import ICalificacionInfoAPeriods = UIUtilViewCalificacion.ICalificacionInfoAPeriods;
import ItemConfigEval = UIUtilViewCalificacion.ItemConfigEval;
import { UIUtilGeneral } from "../../../util/Util";
import { group } from "d3-array";
import { jsPDFOptions } from "../../../../types/jspdf";

export abstract class BoletaBase {
    protected readonly config: IBoletaConfig;
    protected pdfCoordinates: { xPos: number, yPos: number };
    protected widthArea: number;
    protected width: number;
    protected padding: number;
    protected height: number;
    protected auxDivWidth: number;
    protected pdf: jsPDF;
    protected pdfOrientation: jsPDFOptions["orientation"];

    constructor(config: IBoletaConfig) {
        this.config = config;
        this.InitPDF();
        this.auxDivWidth = this.GetPDFAuxDivWidth();
    }

    protected InitPDF() {
        const { PageFormat, TableLineWidth, TableLinesColor, FontFamily } = this.GetBasicPDFStyles();
        this.pdfOrientation = "portrait";
        this.pdf = new jsPDF({ orientation: this.pdfOrientation, unit: "px", format: PageFormat });
        this.pdf.addFont(LatoRegular, 'Lato', 'normal');
        this.pdf.addFont(LatoBold, 'Lato', 'bold');
        const _METADATA = PDFThings._METADATA;

        this.pdf.setProperties({
            title: this.config.MetadataTitle || this.config.Titulo || _METADATA.title,
            subject: _METADATA.subject,
            author: _METADATA.author,
            creator: _METADATA.creator,
            keywords: _METADATA.keywords,
        });

        this.pdf.setLineWidth(TableLineWidth);
        this.pdf.setDrawColor(TableLinesColor);

        this.GetPDFDimensionsCfg();
        this.pdfCoordinates = { xPos: this.padding, yPos: this.padding };
        this.pdf.setFont(FontFamily, "normal");
    }

    protected GetBasicPDFStyles() {
        return {
            PageFormat: "letter",
            PaddingPercentFromWidthPage: 4,
            LogoDimPercentFromWidthPage: 18,
            FontFamily: "Lato", FontSizeTitle: 12, FontSizeSubTitle: 11, FontSizePlain: 10, FontSizeMedium: 9, FontSizeShort: 8, FontColor: "#000",
            TableFillColor: "#fff", TableLinesColor: "#000", TableLineWidth: .1,
            DrawAuxColor: "#d3d3d3",
        }
    }

    protected GetBasicPDFTableStyles() {
        const { FontFamily, FontSizeMedium, FontColor, TableFillColor, TableLinesColor, TableLineWidth } = this.GetBasicPDFStyles();
        return {
            font: FontFamily, fontSize: FontSizeMedium, textColor: FontColor,
            fillColor: TableFillColor, lineColor: TableLinesColor, lineWidth: TableLineWidth,
        }
    }

    protected GetPDFDimensionsCfg() {
        const PAGE_WIDTH: number = this.pdf.internal.pageSize.getWidth();
        const PAGE_HEIGHT: number = this.pdf.internal.pageSize.getHeight();
        const PAGE_PADDING = (PAGE_WIDTH / 100 * this.GetBasicPDFStyles().PaddingPercentFromWidthPage);

        this.padding = PAGE_PADDING;
        this.widthArea = PAGE_WIDTH - PAGE_PADDING * 2;
        this.width = PAGE_WIDTH;
        this.height = PAGE_HEIGHT;

        return {
            Padding: PAGE_PADDING,
            Height: PAGE_HEIGHT,
            Width: PAGE_WIDTH,
            LogoWidth: 0,
            WidthArea: PAGE_WIDTH - PAGE_PADDING * 2,
            HeightArea: PAGE_HEIGHT - PAGE_PADDING * 2
        }
    }

    protected GetPDFAuxDivWidth() {
        const { WidthArea } = this.GetPDFDimensionsCfg();
        return WidthArea / 6;
    }

    protected async DrawLogo(): Promise<number> {
        const { Logo } = this.config;
        let logoWidth = this.GetLogoWidth();
        let heightLogo = 0;
        try {
            const img = await UIUtilGeneral._GetIMGElementOnLoadResource(Logo);
            heightLogo = img.height * logoWidth / img.width;
            if (heightLogo > 70) {
                heightLogo = 70;
                logoWidth = img.width * heightLogo / img.height;
            }
            this.pdf.addImage(img, "JPEG", this.pdfCoordinates.xPos, this.pdfCoordinates.yPos, logoWidth, heightLogo);
        } catch {
            heightLogo = 50;
            console.warn("-d Fail img LOAD");
        }
        return heightLogo;
    }

    protected DrawQualifications() {
        const { MateriasCalificacionesAllPeriods } = this.config;
        const subjectQualifsGrouppedByEvalType = Array.from(group(MateriasCalificacionesAllPeriods, d => d.TipoEval).values());
        for (const evalTypeSubjectsQualifs of subjectQualifsGrouppedByEvalType) {
            const { TipoEval } = evalTypeSubjectsQualifs[0];
            switch (TipoEval) {
                case CTipoEvaluacion.Cualitativa:
                case CTipoEvaluacion.Numeros:
                    this.DrawQualificationTbl(evalTypeSubjectsQualifs, TipoEval);
                    break;
                default:
                    const evalCfgSubjectsQualificationsMap = new Map<number, UIUtilViewCalificacion.MateriasPerEvalCfgV2>();
                    evalTypeSubjectsQualifs.forEach((subjectsQualifications, i) => {
                        const { EvalDescripcion, EvalValor } = subjectsQualifications
                        const subjectEvalCfg = subjectsQualifications.EvalValor.map<ItemConfigEval>((_, i) => {
                            return {
                                Valor: EvalValor[i],
                                Descripcion: EvalDescripcion[i],
                            }
                        })
                        let hasEvalCfgCoincidence = false;
                        for (const [_, subjectQualifs] of evalCfgSubjectsQualificationsMap) {
                            if (hasEvalCfgCoincidence) continue;
                            if (subjectQualifs.ConfigEval.length != subjectEvalCfg.length) hasEvalCfgCoincidence = false;
                            else {
                                hasEvalCfgCoincidence = subjectEvalCfg.every(itemCfg => { return subjectQualifs.ConfigEval.some(item => itemCfg.Valor.trim().toLowerCase() == item.Valor.trim().toLowerCase()) })
                                if (hasEvalCfgCoincidence) { subjectQualifs.MateriasCalificadas.push(subjectsQualifications) }
                            }
                        }
                        if (!hasEvalCfgCoincidence) { evalCfgSubjectsQualificationsMap.set(i, { ConfigEval: subjectEvalCfg, MateriasCalificadas: [subjectsQualifications] }) }
                    })
                    evalCfgSubjectsQualificationsMap.forEach((evalCfgSubjectQualifs) => {
                        this.DrawQualificationTbl(evalCfgSubjectQualifs.MateriasCalificadas, TipoEval, evalCfgSubjectQualifs.ConfigEval);
                    })
                    break;
            }
        }
    }

    protected DidParseCellManage(data: CellHookData, evalType: CTipoEvaluacion) {
        const heightTextAux = this.pdf.getTextDimensions("T").h;
        if (!this.IsCellAbleToDrawContent(evalType, data)) return;
        data.cell.text = [];
        const nEvals = (data.cell.raw as string).split("\n").length;
        data.cell.styles.minCellHeight = 3 + ((nEvals * ((heightTextAux - 2) * 2)) + (nEvals * 2)) + 1;
    }

    protected DidDrawCellManage(data: CellHookData, evalType: CTipoEvaluacion) {
        if (!this.IsCellAbleToDrawContent(evalType, data)) return;
        this.DrawCircleEvalInCell(data, evalType);
    }

    protected DrawCircleEvalInCell(data: CellHookData, evalType: CTipoEvaluacion) {
        const { DrawAuxColor } = this.GetBasicPDFStyles();
        const heightTextAux = this.pdf.getTextDimensions("T").h;

        this.pdf.setDrawColor(DrawAuxColor);
        const cellWidth = data.cell.width;
        const cellHeight = data.cell.height;
        const centerX = data.cell.x + cellWidth / 2;

        const evaluations = (data.cell.raw as string).split("\n");
        const nEvals = evaluations.length;
        const heightCirclesGroup = ((nEvals * ((heightTextAux - 2) * 2)) + ((nEvals - 1) * 2));
        let PosY = data.cell.y + ((cellHeight - heightCirclesGroup) / 2);
        const radius = heightTextAux - 2;
        evaluations.forEach(value => {
            this.pdf.setFillColor(value);
            this.pdf.circle(centerX, PosY + radius, radius, 'DF');
            PosY += (radius * 2) + 2
        });
    }

    protected IsCellAbleToDrawContent(evalType: CTipoEvaluacion, data: CellHookData) {
        return (
            evalType == CTipoEvaluacion.Colores &&
            data.cell.raw &&
            typeof data.cell.raw === 'string' &&
            data.cell.raw.split("")[0] == "#"
        );
    }

    protected DrawPageNumbers() {
        const { FontSizePlain } = this.GetBasicPDFStyles();

        var pageCount = this.pdf.getNumberOfPages();
        const devDrawGuideLines = false;
        for (let i = 0; i < pageCount; i++) {
            this.pdf.setPage(i);
            this.pdf.setFontSize(FontSizePlain);
            let pageCurrent = this.pdf.getCurrentPageInfo().pageNumber; // Current Page
            this.GetPDFDimensionsCfg();

            if (devDrawGuideLines) {
                this.pdf.setDrawColor("#F95410");
                this.pdf.line(this.padding, 0, this.padding, this.height);
                this.pdf.line(this.width - this.padding, 0, this.width - this.padding, this.height);
                this.pdf.line(0, this.padding, this.width, this.padding);
                this.pdf.line(0, this.height - this.padding, this.width, this.height - this.padding);
            }

            this.pdf.text(
                `${pageCurrent}`,
                (this.width - this.padding - this.pdf.getTextDimensions(pageCurrent + "").w),
                (this.height - this.padding + this.pdf.getTextDimensions("1").h),
            );
        }
    }

    protected AddFixedOrientationPage(orientation: jsPDFOptions["orientation"]) {
        this.pdfOrientation = orientation;
        this.pdf.addPage("letter", this.pdfOrientation);
        this.pdf.setPage(this.pdf.getNumberOfPages());
        this.GetPDFDimensionsCfg();
        this.auxDivWidth = this.GetPDFAuxDivWidth();
    }

    protected abstract GetLogoWidth(): number;

    protected abstract DrawBoletaContent(): void;

    protected abstract DrawQualificationTbl(evalTypeSubjectsQualifs: ICalificacionInfoAPeriods[], evalType: CTipoEvaluacion, evalCfg?: ItemConfigEval[]): void;
}